import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLibrary, type ItemComment } from './LibraryContext';

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

/**
 * The comments on one item and a box to add one. Comments are notes to
 * yourself about why you kept something; they stay with the item wherever
 * it moves. Used in the comments sheet (long-press, Comment) and under the
 * actions in the detail view.
 */
export function ItemComments({ itemId, autoFocus = false, className }: { itemId: string; autoFocus?: boolean; className?: string }) {
  const { comments } = useLibrary();
  const [list, setList] = useState<ItemComment[] | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const listApi = comments.list;

  useEffect(() => {
    let cancelled = false;
    setList(null);
    listApi(itemId).then((rows) => {
      if (!cancelled) setList(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [itemId, listApi]);

  // Grow with the text up to five lines
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 5 * 22 + 20)}px`;
  }, [text]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const comment = await comments.add(itemId, body);
      if (comment) {
        setList((prev) => [...(prev ?? []), comment]);
        setText('');
      }
    } finally {
      setSending(false);
    }
  };

  const remove = async (comment: ItemComment) => {
    if (await comments.remove(comment)) setList((prev) => (prev ?? []).filter((c) => c.id !== comment.id));
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {list === null ? (
        <p className="px-1 text-[13px] text-ink-3">Loading comments</p>
      ) : list.length === 0 ? (
        <p className="px-1 text-[13px] text-ink-3">No comments yet. Note why you kept this.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {list.map((c) => (
            <li key={c.id} className="group relative rounded-[12px] border border-line bg-paper-raised px-3.5 py-2.5">
              <p className="whitespace-pre-line [overflow-wrap:anywhere] pr-6 text-[15px] leading-[22px] text-ink">{c.body}</p>
              <p className="mt-1 font-mono text-[11px] text-ink-3">{when(c.createdAt)}</p>
              <button
                type="button"
                onClick={() => remove(c)}
                aria-label="Delete comment"
                className="absolute right-1.5 top-1.5 flex h-8 w-8 items-center justify-center rounded-full text-ink-3 outline-none active:bg-wash focus-visible:ring-2 focus-visible:ring-signal"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex items-end gap-2 rounded-[22px] border border-line bg-paper-raised py-1.5 pl-4 pr-1.5 focus-within:border-signal"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          ref={boxRef}
          autoFocus={autoFocus}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
          rows={1}
          placeholder="Add a comment"
          aria-label="Add a comment"
          className="max-h-[130px] min-h-[36px] flex-1 resize-none bg-transparent py-[7px] text-[15px] leading-[22px] text-ink outline-none placeholder:text-ink-3"
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          aria-label="Post comment"
          className="mb-px flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-paper outline-none transition-[opacity,transform] duration-300 ease-pop active:scale-[0.94] active:duration-100 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-signal motion-reduce:transition-none motion-reduce:active:scale-100"
        >
          <ArrowUp className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </form>
    </div>
  );
}
